// Static blog posts data - Add new posts here
// To add a new post:
// 1. Copy the post template below
// 2. Add it to the POSTS array
// 3. Run: yarn build

export const POSTS = [
  {
    id: "1",
    slug: "getting-started-with-azure-container-apps",
    title: "Getting Started with Azure Container Apps",
    excerpt: "Deploy containerized apps at scale with Azure Container Apps - a fully managed serverless container service that abstracts away infrastructure.",
    category: "Azure",
    tags: ["azure", "containers", "serverless", "devops"],
    cover_image: "",
    created_at: "2024-01-15",
    reading_time: 3,
    content: `# Getting Started with Azure Container Apps

Azure Container Apps is a fully managed serverless container service that lets you run containerized applications without managing complex infrastructure.

## Why Azure Container Apps?

- **Serverless** - Scale to zero when not in use
- **Built-in Dapr** - Simplifies microservices patterns
- **KEDA-based scaling** - Event-driven autoscaling
- **Managed certificates** - HTTPS by default

## Prerequisites

Before you start, make sure you have:
- Azure CLI installed
- Docker installed
- An Azure subscription

## Creating Your First App

\`\`\`bash
# Login to Azure
az login

# Create a resource group
az group create --name my-rg --location eastus

# Create an environment
az containerapp env create \\
  --name my-env \\
  --resource-group my-rg \\
  --location eastus

# Deploy your app
az containerapp create \\
  --name my-app \\
  --resource-group my-rg \\
  --environment my-env \\
  --image mcr.microsoft.com/azuredocs/containerapps-helloworld:latest \\
  --target-port 80 \\
  --ingress external
\`\`\`

## Scaling Configuration

\`\`\`yaml
scale:
  minReplicas: 0
  maxReplicas: 10
  rules:
    - name: http-rule
      http:
        metadata:
          concurrentRequests: "100"
\`\`\`

## Conclusion

Azure Container Apps is an excellent choice for deploying microservices and APIs without the overhead of managing Kubernetes clusters.`
  },
  {
    id: "2",
    slug: "building-rest-api-with-fastapi-and-mongodb",
    title: "Building a REST API with FastAPI and MongoDB",
    excerpt: "FastAPI makes building high-performance REST APIs a breeze with automatic OpenAPI docs, type safety, and async support out of the box.",
    category: "Backend",
    tags: ["python", "fastapi", "mongodb", "api"],
    cover_image: "",
    created_at: "2024-01-20",
    reading_time: 4,
    content: `# Building a REST API with FastAPI and MongoDB

FastAPI is a modern, fast web framework for building APIs with Python 3.7+ based on standard Python type hints.

## Why FastAPI?

- **Fast**: Very high performance, on par with NodeJS and Go
- **Automatic docs**: OpenAPI and ReDoc generated automatically
- **Type safety**: Based on Pydantic v2
- **Async**: Native async/await support

## Project Setup

\`\`\`bash
pip install fastapi uvicorn motor python-dotenv
\`\`\`

## Defining Models

\`\`\`python
from pydantic import BaseModel
from typing import Optional

class PostCreate(BaseModel):
    title: str
    content: str
    category: str = "General"
    published: bool = False
\`\`\`

## Creating CRUD Endpoints

\`\`\`python
from fastapi import FastAPI, HTTPException
from motor.motor_asyncio import AsyncIOMotorClient

app = FastAPI()
client = AsyncIOMotorClient("mongodb://localhost:27017")
db = client["mydb"]

@app.post("/posts", status_code=201)
async def create_post(post: PostCreate):
    doc = post.model_dump()
    result = await db.posts.insert_one(doc)
    return {"id": str(result.inserted_id), **doc}

@app.get("/posts/{id}")
async def get_post(id: str):
    post = await db.posts.find_one({"_id": ObjectId(id)})
    if not post:
        raise HTTPException(404, "Not found")
    post["id"] = str(post.pop("_id"))
    return post
\`\`\`

## Running the Server

\`\`\`bash
uvicorn main:app --reload
\`\`\`

Visit \`http://localhost:8000/docs\` for the interactive API documentation.

## Best Practices

1. Always use async database operations
2. Validate input with Pydantic models
3. Use dependency injection for auth
4. Return proper HTTP status codes`
  },
  {
    id: "3",
    slug: "cicd-pipeline-with-github-actions-and-azure",
    title: "CI/CD Pipeline with GitHub Actions and Azure",
    excerpt: "Automate your deployments with GitHub Actions. Build, test, and deploy to Azure with every push to main.",
    category: "DevOps",
    tags: ["github-actions", "cicd", "azure", "docker"],
    cover_image: "",
    created_at: "2024-01-25",
    reading_time: 4,
    content: `# CI/CD Pipeline with GitHub Actions and Azure

GitHub Actions makes it easy to automate your software development workflows. In this post, we'll build a complete CI/CD pipeline that deploys to Azure.

## Pipeline Overview

Our pipeline will:
1. Run tests on every push
2. Build a Docker image
3. Push to Azure Container Registry
4. Deploy to Azure Container Apps

## Workflow File

Create \`.github/workflows/deploy.yml\`:

\`\`\`yaml
name: Build and Deploy

on:
  push:
    branches: [main]
  pull_request:
    branches: [main]

env:
  AZURE_CONTAINER_REGISTRY: myregistry.azurecr.io
  IMAGE_NAME: myapp

jobs:
  build-and-test:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4

      - name: Set up Python
        uses: actions/setup-python@v4
        with:
          python-version: '3.11'

      - name: Install dependencies
        run: pip install -r requirements.txt

      - name: Run tests
        run: pytest

  deploy:
    needs: build-and-test
    runs-on: ubuntu-latest
    if: github.ref == 'refs/heads/main'
    
    steps:
      - uses: actions/checkout@v4
      
      - name: Login to ACR
        uses: azure/docker-login@v1
        with:
          login-server: \${{ env.AZURE_CONTAINER_REGISTRY }}
          username: \${{ secrets.ACR_USERNAME }}
          password: \${{ secrets.ACR_PASSWORD }}

      - name: Build and push
        run: |
          docker build -t \${{ env.AZURE_CONTAINER_REGISTRY }}/\${{ env.IMAGE_NAME }}:\${{ github.sha }} .
          docker push \${{ env.AZURE_CONTAINER_REGISTRY }}/\${{ env.IMAGE_NAME }}:\${{ github.sha }}

      - name: Deploy to Container Apps
        uses: azure/container-apps-deploy-action@v1
        with:
          resourceGroup: my-resource-group
          containerAppName: my-app
          imageToDeploy: \${{ env.AZURE_CONTAINER_REGISTRY }}/\${{ env.IMAGE_NAME }}:\${{ github.sha }}
\`\`\`

## Setting Up Secrets

Add these secrets in GitHub -> Settings -> Secrets:

- \`AZURE_CREDENTIALS\` - Service principal JSON
- \`ACR_USERNAME\` - Container registry username
- \`ACR_PASSWORD\` - Container registry password

## Key Takeaways

- Use \`needs\` to chain jobs
- Store secrets safely in GitHub Secrets
- Tag images with commit SHA for traceability`
  },
  {
    id: "4",
    slug: "self-hosted-azure-devops-agent-private-network",
    title: "The Private Network Wall - Why You Need a Self-Hosted Azure DevOps Agent",
    excerpt: "We've all been there. You've built a beautifully secure infrastructure. Your SQL database is tucked away in a private subnet. Then, you try to deploy using a standard Azure DevOps pipeline, and... Timeout.",
    category: "DevOps",
    tags: ["azure", "azure-devops", "self-hosted", "security", "networking"],
    cover_image: "/images/posts/hybrid-devops-architecture.png",
    created_at: "2026-02-27",
    reading_time: 6,
    content: `# The Private Network Wall: Why You Need a Self-Hosted Azure DevOps Agent

We've all been there. You've built a beautifully secure infrastructure. Your SQL database is tucked away in a private subnet. Your Storage Account has "Public access" disabled. Your internal API is only reachable via a VPN.

Then, you try to deploy your code using a standard Azure DevOps pipeline, and... **Timeout.**

The Microsoft-hosted agent is essentially a stranger trying to knock on a door that doesn't exist to the outside world. It can't see your private VNet, it can't talk to your internal servers, and you definitely don't want to open a hole in your firewall just for a deployment. This is where the self-hosted agent stops being an "option" and becomes a necessity.

![Hybrid DevOps Architecture: Cloud Build & Self-Hosted Release](/images/posts/hybrid-devops-architecture.png)

## 1. The Strategy: A Hybrid Two-Stage Approach

One of the most efficient ways to handle this is a two-stage pipeline. We don't need to do everything on the private agent; we only use it where it counts.

- **Stage 1: Build (Microsoft-Hosted):** We use the standard Azure DevOps host pool to build the application, restore dependencies, and run tests. This keeps the "heavy lifting" on Microsoft's hardware and stores the artifacts securely.
- **Stage 2: Release (Self-Hosted):** This is where we switch to our self-hosted agent. Because this agent lives _inside_ your private network, it can pull those artifacts and deploy them directly to your **Azure Functions, Internal APIs, and Web Apps** with private endpoints.

## 2. Why Go Self-Hosted? (It's Not Just About Avoiding the Queue)

Microsoft-hosted agents are convenient "disposable" VMs, but they have a massive blind spot: **Isolation.**

- **Networking & Security:** If your resources are behind a private firewall or VNet, a hosted agent is blind. A self-hosted agent sitting inside your network has an "all-access pass" without compromising security.
- **Custom Dependencies:** Tired of waiting 5 minutes for your pipeline to install a niche SDK or a legacy binary? Install it once on your self-hosted machine, and it's there forever.
- **Blazing Fast Builds:** Because self-hosted agents persist data, your \`npm install\` or \`nuget restore\` takes seconds, not minutes, thanks to local caching.

## 3. Setting Up Your "Inside Man"

To get this working, you don't need to open your firewall to the world. You just need to follow three main steps.

### Step 1: The "Secret Handshake" (PAT)

Azure DevOps needs to trust your machine. To do this, you need a Personal Access Token (PAT).

- Go to **User Settings** in Azure DevOps > **Personal access tokens**.
- Select **New Token** and give it the **Agent Pools (Read & Manage)** scope.
- **Copy that token immediately!** It disappears the moment you close the window.

### Step 2: Create a Home (Agent Pool)

In your Organization Settings, head over to **Agent pools**. Create a new pool (e.g., \`Internal-Secure-Pool\`). This is the "waiting room" where your pipelines will look for your agent.

### Step 3: Bring the Agent to Life

Inside your new pool, click **New agent** and download the package for your OS. Run the configuration script (e.g., \`.\\config.cmd\` on Windows).

> **Pro-Tip:** When the script asks "Enter run agent as service?", always say **Y**. You want the agent to start automatically if the server reboots.

## 4. The "Inside Man" Logic: No Inbound Ports Required

One of the biggest misconceptions is that you need to open your firewall to let Azure DevOps "talk" to your server. **You don't.**

The self-hosted agent uses an **outbound-only** connection. It reaches out to Azure DevOps via HTTPS (Port 443) and asks, "Do you have any work for me?" When a job is ready, it pulls the instructions down and executes them locally within your secure perimeter. You get automation without poking holes in your defense.

## 5. The Pro-Tip: Solving Private DNS with the "Hosts File Hack"

Even with an internal agent, you might hit a snag: **DNS.** If you use Private Endpoints, your agent might still try to resolve your App Service URL to a public IP. If your Private DNS zones aren't syncing fast enough, use the VM Hosts File to force a local resolve.

**On your Agent VM, add this line to your hosts file:**

- **Windows:** \`C:\\Windows\\System32\\drivers\\etc\\hosts\`
- **Linux:** \`/etc/hosts\`

\`\`\`
# Map the Private IP to the Deployment (SCM) URL
10.20.0.5      <your-app-name>.scm.azurewebsites.net
\`\`\`

This bypasses the public internet entirely, allowing your agent to push code directly to the private Kudu (SCM) endpoint of your Web App.

## 6. Putting It to Work

In your \`azure-pipelines.yml\`, you simply define which pool to use for each stage. This allows the Build to stay in the cloud while the Deploy stays local:

\`\`\`yaml
stages:
- stage: Build
  pool:
    vmImage: 'ubuntu-latest' # Stage 1: Build in the cloud
  jobs:
  - job: BuildJob
    steps:
    - script: echo "Building..."

- stage: Deploy
  pool:
    name: 'Internal-Secure-Pool' # Stage 2: Deploy from your network
  jobs:
  - job: DeployJob
    steps:
    - script: echo Deploying to my private internal server...
\`\`\`

## The Hardened Security Checklist

Before you go live, make sure your agent is "Enterprise-Ready":

| Item | Status |
|------|--------|
| Run as dedicated service account | ✓ |
| Enable automatic updates | ✓ |
| Use managed identity where possible | ✓ |
| Restrict PAT scope to minimum needed | ✓ |
| Place agent in dedicated subnet | ✓ |

## Summary

Self-hosting your Azure DevOps agent gives you the "home-field advantage." By using a hybrid approach - building in the cloud and releasing from within your network - you get total control, better performance, and a direct line to your private resources without exposing them to the world.

If you're tired of the "spinning wheel of death" while waiting for a hosted agent or fighting with firewall rules, it's time to go self-hosted. Your deployment timeline - and your security team - will thank you.`
  },
  {
    id: "5",
    slug: "cross-project-pipeline-triggers-azure-devops",
    title: "How to Automate Cross-Project Pipeline Triggers in Azure DevOps",
    excerpt: "In a complex DevOps environment, you often need a \"Chain Reaction.\" Learn how to trigger Pipeline B in Project B when Pipeline A in Project A finishes successfully using native Pipeline Resources.",
    category: "DevOps",
    tags: ["azure-devops", "cicd", "pipeline", "automation"],
    cover_image: "",
    created_at: "2026-02-27",
    reading_time: 5,
    content: `# Step by Step: How to Automate Cross-Project Pipeline Triggers Natively in Azure DevOps

In a complex DevOps environment, you often need a "Chain Reaction." For example, after your Infrastructure team in **Project A** finishes a deployment, you want the Application team in **Project B** to automatically start their build.

Many engineers think you need complex API scripts or Service Connections for this, but you can actually do it natively using **Pipeline Resources**.

## The Scenario

- **Project A:** Contains \`Pipeline-A\`.
- **Project B:** Contains \`Pipeline-B\`.
- **Goal:** When \`Pipeline-A\` finishes successfully, \`Pipeline-B\` starts automatically.

## The Architecture

We are working with two distinct projects within the same organization:

1. **Project A (The Source):** Contains \`Pipeline A\`.
2. **Project B (The Target):** Contains \`Pipeline B\`

## Part 1: Create Source and Target Pipeline

### 1. Configure the Source (Project A)

In **Project A**, Your source pipeline doesn't need any special trigger code, but it does need a specific name.

Create a pipeline in repo -> az.yaml

\`\`\`yaml
# Pipeline-A
name: Pipeline-A-Run

trigger:
- main

pool:
  vmImage: 'ubuntu-latest'

steps:
- script: |
    echo "Starting the Upstream Process in Project A..."
    echo "Performing automated tasks..."
    sleep 5
    echo "Tasks completed successfully!"
  displayName: 'Run Upstream Tasks'
\`\`\`

### 2. Configure the Target (Project B)

In **Project B**, you must define **Project A** as a resource. This tells the pipeline to "listen" for a success signal from the other project.

Create a pipeline in repo -> az.yaml

\`\`\`yaml
# Pipeline-B
name: Pipeline-B-Triggered

trigger: none

resources:
  pipelines:
  - pipeline: MyProjectA
    project: 'Project A'  # The EXACT name of Project A
    source: 'Project A'   # The EXACT name of the pipeline in Project A
    trigger:
      branches:
        include:
        - main  # Trigger only when Pipeline A succeeds on 'main'

pool:
  vmImage: 'ubuntu-latest'

steps:
- script: |
    echo "Success! Pipeline B has been triggered by Project A."
    echo "Triggering Pipeline: $(resources.pipeline.MyProjectAAlias.pipelineName)"
    echo "Source Run ID: $(resources.pipeline.MyProjectAAlias.runID)"
  displayName: 'Verify Cross-Project Trigger'
\`\`\`

## Part 2: Permissions & Settings

Even with perfect code, the trigger will fail if you don't adjust two specific security settings. **This is where most people get stuck.**

### Step A: Grant Cross-Project "View" Access

Project B's "Build Service" needs permission to see Project A pipeline.

1. In **Project A**, go to **Pipelines** and click the three dots \`⋮\` on your pipeline.
2. Select **Security**.
3. Search for \`Project B Build Service\`.
4. Set **View build pipeline** to **Allow**.

### Step B: Disable Authorization Scope

By default, Azure DevOps limits pipelines to their own project. You must turn this off in **Project B**.

1. Go to **Project Settings** > **Pipelines** > **Settings**.
2. Set **Limit job authorization scope to current project for non-release pipelines** to **OFF**

## The Result: Automated Success

Once these settings are in place, the flow is fully automated. As soon as \`Pipeline-A-Run\` completes successfully, \`Pipeline-B-Triggered\` will start immediately without any manual intervention.

> **Pro Tip and Mandatory:** Remember to run Pipeline B **manually once** after saving the YAML so Azure DevOps can "register" the new resource trigger!
>
> Note: No need of service connection - azure repo

## Conclusion

Implementing cross-project triggers is a powerful way to automate your release cycle and ensure that dependent services are always in sync. By using native **Pipeline Resources**, you eliminate the need for custom scripts while maintaining a secure, "Success-Only" workflow.`
  },
  {
    id: "6",
    slug: "send-emails-microsoft-graph-postman",
    title: "How to Send Emails via Microsoft Graph Using Postman",
    excerpt: "We've all been there - staring at a 50-line PowerShell script trying to figure out why a simple email won't send. There's a better way using Postman to demystify the Microsoft Graph API.",
    category: "Azure",
    tags: ["microsoft-graph", "azure", "email", "postman", "api"],
    cover_image: "",
    created_at: "2026-02-25",
    reading_time: 5,
    content: `# How to Send Emails via Microsoft Graph Using Postman (No Code Required)

We've all been there: staring at a 50-line PowerShell script, squinting at \`$headers\` and \`Invoke-RestMethod\`, trying to figure out why a simple email won't send. PowerShell is a powerhouse for automation, but when it comes to the "trial and error" phase of development, it can feel like trying to perform surgery while wearing oven mitts.

If you're tired of re-running scripts just to check a single JSON key, there's a better way. Today, we're stepping out of the terminal and into **Postman** to demystify the Microsoft Graph API.

## The Problem

If you've been following the standard documentation for sending emails through the Microsoft Graph API, you've likely run into the common PowerShell scripts using \`Invoke-RestMethod\`. While effective for automation, PowerShell can be a "black box" when you are trying to debug a 403 Forbidden error or a misconfigured Azure permission.

Sometimes, you just want to see the raw JSON response. That's where **Postman** comes in.

In this guide, I'll show you how to take a standard Azure App Registration and send an email using Postman. We will be using the **OAuth 2.0 Client Credentials flow** - perfect for daemon services or background alerts.

## Prerequisites

Before we open Postman, ensure your Azure App Registration has:

1. **Application Permissions:** \`Mail.Send\` (not Delegated).
2. **Admin Consent:** A Global Admin must click "Grant admin consent for [Your Org]."
3. **Client Secret:** A valid secret generated under "Certificates & Secrets."

## Phase 1: The Handshake (Getting your Token)

Unlike a browser login, an App Registration needs to "ask" for a token using its credentials.

1. **Create a New Request:** Set the method to \`POST\`.
2. **URL:** \`https://login.microsoftonline.com/{{Your_Tenant_ID}}/oauth2/v2.0/token\`
3. **The Body:** Navigate to the **Body** tab, select **x-www-form-urlencoded**, and enter these four keys:

| Key | Value |
|-----|-------|
| \`client_id\` | Your Application (client) ID |
| \`scope\` | \`https://graph.microsoft.com/.default\` |
| \`client_secret\` | Your Client Secret value |
| \`grant_type\` | \`client_credentials\` |

Hit **Send**. You'll receive a JSON response containing a massive string called \`access_token\`. Copy that entire string.

## Phase 2: The Delivery (Sending the Email)

Now that we have our "digital ID badge," we can tell Microsoft to send the mail.

1. **New Request:** Set the method to \`POST\`.
2. **URL:** \`https://graph.microsoft.com/v1.0/users/{{Sender_Email}}/sendMail\`
   
   *Note: Replace {{Sender_Email}} with the actual mailbox you want to send from.*

3. **The Auth:** Go to the **Authorization** tab, select **Bearer Token**, and paste your token.
4. **The Payload:** Go to **Body**, select **raw**, and choose **JSON**. Paste this structure:

\`\`\`json
{
  "message": {
    "subject": "Testing Microsoft Graph via Postman",
    "body": {
      "contentType": "Text",
      "content": "If you are reading this, your setup is working!"
    },
    "toRecipients": [
      {
        "emailAddress": {
          "address": "your-personal-email@example.com"
        }
      }
    ]
  }
}
\`\`\`

## Why Use Postman Over PowerShell?

While PowerShell is king for scripts, Postman offers:

- **Visual Debugging:** You can see exactly which header or body parameter is causing a failure.
- **History:** Easily revisit previous tests without re-typing complex commands.
- **Collections:** Share your API setup with your team so they can test their own Azure apps instantly.

## Troubleshooting Tip

If you get a **"Resource not found"** error, double-check your URL. When using Client Credentials, you cannot use \`/me/sendMail\` because the "App" doesn't have a "me." You must specify the user ID or email: \`/users/{id}/sendMail\`.

## Summary

Testing your Microsoft Graph integrations shouldn't be a headache. By moving your testing from a terminal into Postman, you gain better visibility and faster iteration.

Alternative Option: [Use Graph Explorer to try Microsoft Graph APIs](https://learn.microsoft.com/en-us/graph/graph-explorer/graph-explorer-overview)`
  },
  {
    id: "7",
    slug: "bicep-vs-terraform-azure-2026",
    title: "Azure Infrastructure - Should You Choose Bicep or Terraform in 2026",
    excerpt: "We've all been there. You're starting a new Azure project, the architecture diagram is beautiful, and then comes the \"Infrastructure Tool\" question - Bicep or Terraform?",
    category: "Azure",
    tags: ["azure", "terraform", "bicep", "iac", "cloud-strategy"],
    cover_image: "",
    created_at: "2026-02-19",
    reading_time: 6,
    content: `# Azure Infrastructure: Should You Choose Bicep or Terraform in 2026

We've all been there. You're starting a new Azure project, the architecture diagram is beautiful, and then comes the "Infrastructure Tool" question: **Bicep or Terraform?**

In 2026, the "Infrastructure as Code" (IaC) isn't about which tool is better - it's about what kind of life you want to live as a DevOps engineer. Do you want the "all-in" native experience, or do you want a tool that works everywhere?

Let's break down the reality of these two giants.

## The Philosophy: Native vs. Agnostic

If **Bicep** is a custom-tailored suit specifically for Azure, **Terraform** is a high-end leather jacket that looks good in any club (AWS, GCP, or Azure).

- **Bicep** is Microsoft's love letter to Azure. It's a DSL (Domain Specific Language) that sits right on top of ARM. If Azure releases a new feature at 9:00 AM, Bicep supports it at 9:01 AM.
- **Terraform** is the industry standard for a reason. It uses HCL (HashiCorp Configuration Language) and focuses on "State." It doesn't care if you're deploying a VM in Azure or a DNS record in Cloudflare - it handles it all in one workflow.

## When to Use Which? (The Honest Take)

### Go with Bicep if:

You are an Azure shop through and through. You don't want to worry about "state files" (the dreaded \`.tfstate\`), and you want your code to feel like a natural extension of the Azure portal. It's simpler, leaner, and-honestly-the error messages are usually better because they come directly from the Azure API.

### Go with Terraform if:

You live in a multi-cloud world. If your app uses Azure for compute but Snowflake for data and PagerDuty for alerts, Terraform is your only real choice. It allows you to stitch different providers together into a single, cohesive deployment.

## Advanced Edge: Case by Case

Choosing a winner isn't about which tool is smarter; it's about which tool is smarter for **your specific problem**.

| Scenario | Winner |
|----------|--------|
| Azure-only environment | Bicep |
| Multi-cloud deployment | Terraform |
| Need latest Azure features immediately | Bicep |
| Team already knows HCL | Terraform |
| Want to avoid state management | Bicep |
| Complex resource dependencies | Terraform |

At the end of the day, both tools are **declarative**. You aren't writing a script to "Create a Disk"; you're writing a document that says, "I want a Disk to exist."

They both support:

- **Modules:** To keep your code from becoming a 5,000-line nightmare.
- **Visualizers:** Both have great VS Code extensions that make your code look like a work of art.
- **CI/CD:** Both play perfectly with GitHub Actions and Azure DevOps.

## The "Switch" Factor: Can you learn both?

If you already know one, you're 80% of the way to knowing the other.

- **From Terraform to Bicep:** You'll feel like a weight has been lifted. No more managing remote backends or locking state files. The syntax is "looser" and feels a bit like TypeScript.
- **From Bicep to Terraform:** You'll have to learn the "State" discipline. You can't just delete code and expect the resource to vanish without understanding how Terraform tracks it. But once you get the hang of HCL, you'll feel like you have a superpower that works on any cloud.

## Code Comparison: A Side-by-Side Look

Let's look at how we'd deploy a standard **Azure Storage Account**.

### Terraform

\`\`\`hcl
# We need to tell Terraform we're talking to Azure
provider "azurerm" {
  features {}
}

# Variable definition
variable "storage_name" {
  default = "mysto2026tf"
}

# The actual resource
resource "azurerm_storage_account" "sto" {
  name                     = var.storage_name
  resource_group_name      = "my-resource-group"
  location                 = "East US"
  account_tier             = "Standard"
  account_replication_type = "LRS"
}
\`\`\`

### Bicep

\`\`\`bicep
// Target Scope deployment -> resourceGroup, subscription, managementGroup
targetScope = 'resourceGroup'

// Parameter definition
param storageName string = 'mysto2026bicep'

// The actual resource
resource storageAccount 'Microsoft.Storage/storageAccounts@2023-01-01' = {
  name: storageName
  location: 'eastus'
  sku: {
    name: 'Standard_LRS'
  }
  kind: 'StorageV2'
}
\`\`\`

### What stands out here?

- **API Versions:** In **Bicep**, you see that \`@2023-01-01\` string. You are explicitly picking the Azure API version. In **Terraform**, the provider handles that mapping behind the scenes.
- **Verbosity:** Bicep is generally shorter. You don't have to define a "Provider" block, and the syntax for properties (like \`sku\`) is slightly more condensed.
- **The "Feel":** Terraform feels like a **configuration file**. Bicep feels like a **programming script**.

## Deployment Commands

| Tool | Command |
|------|---------|
| Terraform | \`terraform init && terraform plan && terraform apply\` |
| Bicep | \`az deployment group create --template-file main.bicep\` |

## Final Verdict

*"The 2026 playbook is simple: **Go Terraform for your foundations and Bicep for your Azure-native apps.** Organizations that juggle multiple clouds or third-party providers need Terraform's orchestration power. But if your heart (and your workload) belongs to Azure, Bicep offers a frictionless, state-free experience that's hard to beat. Use Terraform for the big picture, and Bicep for the fine details"*

Stop overthinking the syntax. Pick the tool that fits your **cloud strategy**, not just your favorite curly-bracket style.`
  },
  {
    id: "8",
    slug: "terraform-module-explained-with-example",
    title: "What Is a Terraform Module - Simple Explanation With Example",
    excerpt: "A Terraform module is a reusable block of Terraform code. In simple words - Write once, use many times. Think of it like a building blueprint.",
    category: "DevOps",
    tags: ["terraform", "modules", "iac", "infrastructure-as-code"],
    cover_image: "",
    created_at: "2026-02-13",
    reading_time: 5,
    content: `# What Is a Terraform Module? (Simple Explanation With Example)

> A Terraform module is a **reusable block of Terraform code**.

In simple words: **Write once, use many times**

## Example 1: Blueprint Example

Think of a **building blueprint**.

- A blueprint shows how to build a house
- You can use the same blueprint to build many houses
- You can share the blueprint with different builders

A **Terraform module** is like that blueprint.

- Blueprint -> Terraform module
- House -> Cloud resource (VM, network, storage)
- Builder -> Terraform

You define the blueprint once, and Terraform builds it wherever you need.

## What Problem Do Terraform Modules Solve?

**Without modules:**
- You copy-paste the same Terraform code
- Code becomes messy
- Changes are hard
- Mistakes happen easily

**With modules:**
- Code is clean
- Easy to reuse
- Easy to manage
- Same standard everywhere -> Security, Convention across each environments

## Key Concepts of Terraform Modules

**1. Root Module/Root Main Folder:** A main working directory where you run Terraform commands: \`terraform init\`, \`terraform plan\`, \`terraform apply\`

**2. Child Module:** A set of the standalone configuration file called by the Root Module or other Module.

## Structure

\`\`\`
project/
├── main.tf              # Root module
├── variables.tf
├── outputs.tf
├── providers.tf
├── env/
│   ├── dev.tfvars
│   └── prod.tfvars
└── modules/
    └── vm/
        ├── main.tf      # Child module
        ├── variables.tf
        ├── outputs.tf
        └── provider.tf
\`\`\`

Good structure = easy Terraform
Bad structure = headache

## Subnet Module Example

### modules/vm/main.tf

\`\`\`hcl
resource "azurerm_linux_virtual_machine" "vm" {
  name                = var.vm_name
  location            = var.location
  resource_group_name = var.resource_group_name
  size                = var.vm_size
  admin_username      = var.admin_username
  admin_password      = var.admin_password
}
\`\`\`

### modules/vm/outputs.tf

\`\`\`hcl
output "vm_id" {
  value = azurerm_linux_virtual_machine.vm.id
}

output "vm_name" {
  value = azurerm_linux_virtual_machine.vm.name
}
\`\`\`

### modules/vm/variables.tf

\`\`\`hcl
variable "vm_name" {}
variable "location" {}
variable "resource_group_name" {}
variable "vm_size" {}
variable "admin_username" {}
variable "admin_password" {
  sensitive = true
}
\`\`\`

## Using the Module (Root main.tf)

\`\`\`hcl
module "vm" {
  source              = "./modules/vm"
  vm_name             = var.vm_name
  location            = "East US"
  resource_group_name = "rg-demo"
  vm_size             = "Standard_B2s"
  admin_username      = "azureuser"
  admin_password      = var.admin_password
}
\`\`\`

### env/dev.tfvars

\`\`\`hcl
# Note: Use the variable groups from pipeline, or use the key vault
admin_password = "<Password@1234>"
vm_name = "dev-vm"
\`\`\`

### env/prod.tfvars

\`\`\`hcl
admin_password = "<Password@1234>"
vm_name = "prod-vm"
\`\`\`

## Deployment Flow

- Terraform runs **from the Root folder**
- Root calls required **modules**
- *Optional*: Values are passed using \`.tfvars\`

\`\`\`bash
terraform init
terraform plan -var-file=dev.tfvars
terraform apply -var-file=dev.tfvars
terraform destroy -var-file=dev.tfvars
\`\`\`

The root module controls deployment, modules define reusable infrastructure, and \`.tfvars\` files manage environments.

## Conclusion

Using modules in Terraform is a powerful way to manage infrastructure. It simplifies the code and makes it more maintainable by avoiding repetition. This approach is particularly useful for creating multiple resources that share similar characteristics but have unique configurations.`
  },
  {
    id: "9",
    slug: "azure-ad-app-registration-expiry-alerts",
    title: "Automating Azure AD App Registration Secret and Certificate Expiry Alerts",
    excerpt: "Managing secrets and certificates in Azure AD app registrations is crucial to maintaining security. Learn how to automate expiry notifications using Logic Apps and Automation Account Runbook.",
    category: "Azure",
    tags: ["azure", "automation", "logic-apps", "powershell", "security"],
    cover_image: "",
    created_at: "2025-02-10",
    reading_time: 7,
    content: `# Automating Azure AD App Registrations Secret and Certificate Expiry Alerts with Logic Apps

## Introduction

Managing secrets and certificates in Azure Active Directory (Azure AD) app registrations is crucial to maintaining security and compliance. Expired secrets can cause unexpected outages, while early warnings allow teams to rotate credentials before issues arise.

In this article, we'll walk through an **Azure Automation Account Runbook** written in **PowerShell 7.2** that retrieves Azure AD **app registrations**, identifies **secrets and certificates** nearing expiration, and generates an **HTML table** for email notifications. The process depends on **Azure Logic Apps** for automation.

## Prerequisites

To implement this solution, you need:

- An **Azure Automation Account** with a **Managed Identity** enabled
- Runbook Script **PowerShell Version 7.2**
- The **Azure AD module** imported into the Automation Account
- **Azure Logic Apps** configured schedule (Recurrence) and to send emails

## PowerShell Runbook Overview

The PowerShell script performs the following actions:

1. **Authenticates** to Azure using Managed Identity.
2. **Fetches app registrations** and retrieves their associated secrets and certificates.
3. **Formats the data** into an HTML table.
4. **Highlights upcoming expirations** with color-coded alerts.

## PowerShell Script 7.2

\`\`\`powershell
#######################################################
###     PowerShell: 7.2 (Preview)
###     Output: Generate HTML table body for the Mail
###     Depends on Logic Apps
#######################################################

try {
    Connect-AzAccount -Identity | Out-Null
} catch {
    Write-Error -Message $_.Exception
    throw $_.Exception
}

# Get Application details
$appRegistrations = Get-AzADApplication | Select-Object -Property AppId, DisplayName, PasswordCredentials, keyCredentials

# Store results in an array
$obj = @()
foreach ($app in $appRegistrations) {
    # Process the secrets
    $secrets = @()
    foreach ($secret in $app.PasswordCredentials) {
        $secrets += @{
            "AppID"         = $app.AppId
            "AppName"       = $app.DisplayName
            "Name"          = $secret.DisplayName
            "Type"          = "Secret"
            "StartDateTime" = $secret.StartDateTime.ToString("MM/dd/yyyy")
            "EndDateTime"   = $secret.EndDateTime.ToString("MM/dd/yyyy")
            "RemainingDays" = ($secret.EndDateTime - (Get-Date)).Days
        }
    }
    $obj += $secrets

    # Process the certificates
    $certificates = @()
    foreach ($certficate in $app.KeyCredentials) {
        $certificates += @{
            "AppID"         = $app.AppId
            "AppName"       = $app.DisplayName
            "Name"          = $certficate.DisplayName
            "Type"          = "Certificate"
            "StartDateTime" = $certficate.StartDateTime.ToString("MM/dd/yyyy")
            "EndDateTime"   = $certficate.EndDateTime.ToString("MM/dd/yyyy")
            "RemainingDays" = ($certficate.EndDateTime - (Get-Date)).Days
        }
    }
    $obj += $certificates
}

# Sort the object with remaining days
$obj = $obj | Sort-Object -Property { [int]$_.RemainingDays }

# Build HTML table with color-coded alerts
# Black: Expired
# Red: Within 15 days
# Yellow: Within 30 days
# No color: Within 60 days
\`\`\`

## Logic App Workflow Overview

The Logic App consists of the following steps:

### 1. Recurrence Trigger
- Defines how frequently the automation runs (e.g., daily, monthly)
- Configured with the time zone (UTC +5:30 for India)

### 2. Create Job in Azure Automation
- Triggers the Azure Automation Runbook
- Uses Managed Identity for authentication

### 3. Get Job Output
- Retrieves the results of the runbook execution (the HTML table)
- Ensures the output is correctly fetched before sending an email

### 4. Send Email Notification
- Uses Outlook or SMTP connector to send an email
- The body contains the HTML table with expiry details
- The subject and importance level are set for clarity

## Step by Step Configuration

### Step 1: Add a Recurrence Trigger

1. In the Logic App Designer, search for \`Recurrence\`
2. Set **Interval** to \`1\` and **Frequency** to \`Day\`
3. Set **Time Zone** (e.g., \`UTC+05:30\` for India)

### Step 2: Add "Azure Automation - Create Job" Action

1. Click **New Step** > Search for \`Azure Automation\`
2. Select **Create Job**
3. Configure the following:
   - **Subscription**: Choose your Azure subscription
   - **Resource Group**: Select the resource group
   - **Automation Account**: Pick the existing Automation Account
   - **Runbook Name**: Enter the PowerShell Runbook name
   - **Wait for Job**: \`Yes\`
   - **Authentication**: Choose **System-assigned Managed Identity**

### Step 3: Add "Get Job Output" Action

1. Click **New Step** > Search for \`Azure Automation\`
2. Select **Get Job Output**
3. Configure with same Subscription, Resource Group, and Automation Account
4. **Job ID**: Select the output from the previous step

### Step 4: Add "Send an Email" Action

1. Click **New Step** > Search for \`Outlook 365\` or \`SMTP\`
2. Select **Send an email (V2)**
3. Configure:
   - **To**: Enter recipients
   - **Subject**: \`App registration secrets and certificates - Close to Expiration\`
   - **Body**: Insert HTML table output from \`Get Job Output\`
   - **Importance**: Set to \`High\`

## Visual Color Indicators

| Color | Meaning |
|-------|---------|
| **Black** | The app registration has already expired |
| **Red** | Critically close to expiry (within 15 days) |
| **Yellow** | Will expire within 30 days |
| **No Color** | Valid but will expire within 60 days |

## Conclusion

By implementing this **PowerShell Runbook in Azure Automation**, you can effectively monitor Azure AD **app registration secrets and certificates**. The **automated email alerts** ensure timely action before secrets expire, preventing security risks and service disruptions.`
  },
  {
    id: "10",
    slug: "terraform-dynamic-foreach-lookup-guide",
    title: "Using Dynamic for_each with lookup in Terraform - Practical Examples Guide",
    excerpt: "The for_each and lookup features in Terraform are powerful tools for managing complex configurations, especially when dealing with nested resources.",
    category: "DevOps",
    tags: ["terraform", "foreach", "lookup", "dynamic", "iac"],
    cover_image: "",
    created_at: "2024-08-04",
    reading_time: 6,
    content: `# Using Dynamic 'for_each' with 'lookup' in Terraform: Practical Examples Guide

The \`for_each\` and \`lookup\` features in Terraform are powerful tools for managing complex configurations, especially when dealing with nested resources.

## Introduction

When working with Terraform, utilizing the \`for_each\` and \`lookup\` functions can significantly simplify and optimize your infrastructure as code. These functions allow you to dynamically create multiple resources without repeating code blocks, making your configuration cleaner and more efficient but it will add more complexity to the code.

In this article, we'll explore how to use \`for_each\` and \`lookup\` together in Terraform, along with practical examples relating to modules.

## Structure

\`\`\`
project/
├── main.tf
├── variables.tf
├── outputs.tf
├── providers.tf
└── modules/
    └── vnet/
        ├── main.tf
        ├── variables.tf
        ├── outputs.tf
        └── provider.tf
\`\`\`

## Subnet Module

### modules/vnet/main.tf

\`\`\`hcl
resource "azurerm_virtual_network" "vnet" {
  name                = var.m_vnet_name
  address_space       = var.m_vnet_address_space
  location            = var.m_location
  resource_group_name = var.m_rg_name
}

resource "azurerm_subnet" "subnets" {
  for_each             = var.m_subnets
  name                 = each.key
  virtual_network_name = azurerm_virtual_network.vnet.name
  resource_group_name  = azurerm_virtual_network.vnet.resource_group_name
  address_prefixes     = each.value.subnet_address_prefixes

  dynamic "delegation" {
    for_each = lookup(each.value, "delegation", [])
    content {
      name = lookup(delegation.value, "name", null)

      dynamic "service_delegation" {
        for_each = lookup(delegation.value, "service_delegation", [])
        content {
          name    = lookup(service_delegation.value, "name", null)
          actions = lookup(service_delegation.value, "actions", null)
        }
      }
    }
  }
}
\`\`\`

**In this example:**

- \`for_each\` iterates over the \`var.m_subnets\` map to create a subnet for each entry.
- \`lookup\` retrieves the delegation configuration if it exists; otherwise, it defaults to an empty list.
- The dynamic block \`delegation\` is conditionally created based on the presence of the delegation key in the subnet definition.
- Within the \`delegation\` block, another dynamic block \`service_delegation\` is conditionally created.

### modules/vnet/variables.tf

\`\`\`hcl
variable "m_subnets" {
  type = any
}

variable "m_vnet_name" {
  type = string
}

variable "m_vnet_address_space" {
  type = list(string)
}

variable "m_location" {
  type    = string
  default = "westeurope"
}

variable "m_rg_name" {
  type = string
}
\`\`\`

### modules/vnet/outputs.tf

\`\`\`hcl
output "o_subnet_ids" {
  value = { for s, sub in azurerm_subnet.subnets : s => sub.id }
}
\`\`\`

**Explanation:**
- The \`for\` expression iterates over the \`azurerm_subnet.subnets\` object
- \`s\` represents the key of each subnet, while \`sub\` represents the subnet object itself
- Creates a map where each key is associated with the corresponding subnet ID

## Using the Module (Root main.tf)

\`\`\`hcl
module "vnet" {
  source               = "./modules/vnet"
  m_vnet_name          = "vnet"
  m_rg_name            = "rg-test"
  m_vnet_address_space = ["10.0.0.0/16"]

  m_subnets = {
    hubsubnet = {
      subnet_address_prefixes = ["10.0.1.0/24"]
      delegation = [
        {
          name = "hubdelegation"
          service_delegation = [
            {
              name   = "Microsoft.ContainerInstance/containerGroups"
              action = ["Microsoft.Network/virtualNetworks/subnets/action"]
            }
          ]
        }
      ]
    }
    intsubnet = {
      subnet_address_prefixes = ["10.0.5.0/24"]
    }
  }
}

resource "azurerm_network_interface" "nic" {
  name                = "some-nic"
  location            = "westeurope"
  resource_group_name = "rg-test"

  ip_configuration {
    name                          = "internal"
    subnet_id                     = module.vnet.o_subnet_ids["intsubnet"]
    private_ip_address_allocation = "Dynamic"
  }
}
\`\`\`

### Subnet Definitions Explained

**hubsubnet** - A subnet with delegation:
- \`subnet_address_prefixes\`: ["10.0.1.0/24"]
- \`delegation\`: Configured for Azure Container Instance

**intsubnet** - A simple subnet:
- \`subnet_address_prefixes\`: ["10.0.5.0/24"]
- No delegation (uses lookup default empty list)

## Deployment Commands

\`\`\`bash
terraform init
terraform plan
terraform apply
\`\`\`

## Key Takeaways

1. **\`for_each\`** - Iterates over maps/sets to create multiple resources
2. **\`lookup\`** - Provides safe access to map values with defaults
3. **\`dynamic\` blocks** - Conditionally create nested blocks
4. **Combining all three** - Creates flexible, reusable modules

## Conclusion

Using \`for_each\` with \`lookup\` in Terraform is a powerful way to manage dynamic resource creation. It simplifies the code and makes it more maintainable by avoiding repetition. This approach is particularly useful for creating multiple resources that share similar characteristics but have unique configurations.`
  }
];

// Helper functions for static data access
export const getAllPosts = () => POSTS;

export const getPostBySlug = (slug) => POSTS.find(p => p.slug === slug);

export const getPostsByCategory = (category) => 
  category === 'All' ? POSTS : POSTS.filter(p => p.category === category);

export const searchPosts = (query) => {
  const q = query.toLowerCase();
  return POSTS.filter(p => 
    p.title.toLowerCase().includes(q) ||
    p.excerpt.toLowerCase().includes(q) ||
    p.category.toLowerCase().includes(q) ||
    p.tags.some(t => t.toLowerCase().includes(q))
  );
};

export const getAllCategories = () => ['All', ...new Set(POSTS.map(p => p.category))];

export const getAllTags = () => [...new Set(POSTS.flatMap(p => p.tags))];
